import { useState } from 'react'
import { Box, VStack, HStack, Text, Textarea, Button, Separator } from '@chakra-ui/react'
import type { Order } from '@/common/order.common'
import { toast } from '@/components/ui/toaster/toasterHelpers'
import { LuX } from 'react-icons/lu'
import { rejectOrder } from '@/api/orders.api'

export interface RejectionSectionProps {
  order: Order
  onClose: () => void
  onSuccess: () => void
}

/**
 * RejectionSection
 * ----------------
 * Red panel displayed when a Union manager rejects an order.
 *
 * Matches mockups: declined-order-state1.png / declined-order-state2.png
 */
export default function RejectionSection({ order, onClose, onSuccess }: RejectionSectionProps) {
  const [reason, setReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const isDisabled = reason.trim().length === 0

  const handleReject = async () => {
    if (isSubmitting) return
    try {
      setIsSubmitting(true)

      await rejectOrder(order.id, { reason })

      toast.success('Bestellung abgelehnt', 'Die Bestellung wurde erfolgreich abgelehnt.')

      onSuccess()
    } catch (err: any) {
      toast.error('Fehler', err.message || 'Fehler beim Ablehnen der Bestellung')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Box bg="red.subtle" borderColor="red.muted" borderWidth="1px" rounded="lg" p={4} mt={2}>
      {/* ---------------------------------------------------
       * Header
       * --------------------------------------------------- */}
      <Text fontSize="md" fontWeight="semibold" color="red.fg">
        Begründung *
      </Text>

      <VStack align="stretch" gap={4} mt={4}>
        {/* ---------------------------------------------------
         * Reason text area
         * --------------------------------------------------- */}
        <Box>
          <Textarea
            border={0}
            color="fg.default"
            fontWeight="normal"
            placeholder="Begründung eintragen"
            fontSize="sm"
            value={reason}
            onChange={e => setReason(e.target.value)}
            bg="red.subtle"
          />
          <Separator />
          <Text fontSize="xs" color="fg.muted" mt={2}>
            Bei Ablehnung der Bestellung muss eine Begründung vorliegen.
          </Text>
        </Box>

        {/* ---------------------------------------------------
         * Buttons
         * --------------------------------------------------- */}
        <HStack justify="flex-end" gap={3} pt={1}>
          <Button
            variant="plain"
            fontWeight="semibold"
            onClick={onClose}
            color="red.fg"
            disabled={isSubmitting}
          >
            Abbrechen
          </Button>

          <Button
            colorPalette="red"
            disabled={isDisabled}
            onClick={handleReject}
            loading={isSubmitting}
            loadingText="Wird abgelehnt…"
          >
            <LuX />
            Bestellung endgültig ablehnen
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
